/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  onError,
  onSuccess,
  onSuccessFetch,
  requestPayload,
} from './requestResponse';

/*
 * All these functions can be accessed using the available response keys.
 * data, error, isPending, mutate,....
 *!!!Please use mutate key function to submit data
 */

type ApiResponse = {
  data?: any;
  message?: any;
  error?: any;
  access_token: string;
  detail?: any;
};

function extractErrorMessage(
  errorData: any
): string | undefined {
  // Try to get message from Axios response data first (Backend provided message)
  if (errorData?.response?.data?.message) {
    return errorData.response.data.message;
  }
  // Try to get message from error object directly
  if (errorData?.message) {
    return errorData.message;
  }
  // Fallback to Description if available
  if (errorData?.Description) {
    return errorData.Description;
  }
  return undefined;
}

function extractData(data: ApiResponse['data']) {
  let formattedData;
  let unformattedData;
  if (Array.isArray(data)) {
    formattedData = data[0];
    unformattedData = data;
  } else if (typeof data === 'object' && data !== null) {
    formattedData = data;
  }
  return { formattedData, unformattedData };
}

function extractErrorData(data: ApiResponse) {
  let formattedData;
  return formattedData;
}

export function PostMutate(url?: string, invUrl?: string) {
  const queryClient = useQueryClient();
  let message: string | undefined;
  let successData;
  let errorResponseData;
  let pureData;
  let accessToken;
  let refreshToken;

  const { data, error, isError, isSuccess, isPending, mutate, mutateAsync, reset } =
    useMutation({
      mutationKey: [url],
      mutationFn: requestPayload({ url: url, method: 'POST' }),
      onSuccess: async (response: any) => {
        const formattedData: any = onSuccess(await response);
        successData = formattedData;
        message = formattedData?.Description || formattedData?.message;
        accessToken = formattedData?.access_token;
        queryClient.invalidateQueries({ queryKey: [invUrl] });
      },
      onError: async (error: any) => {
        const errorData = onError(error);
        errorResponseData = errorData;
        message = extractErrorMessage(await errorData);
      },
    });

  const errorData: ApiResponse['error'] | undefined = error;
  const responseData: ApiResponse['data'] | undefined = data;

  if (errorData) {
    message = extractErrorMessage(errorData);
    errorResponseData = extractErrorData(errorData);
  }
  if (responseData) {
    successData = extractData(responseData?.data).formattedData;
    pureData = extractData(responseData?.data).unformattedData;
    message = successData?.message;
    accessToken = successData?.data?.access_token;
    refreshToken = successData?.data?.refresh_token;
  }

  return {
    pureData: pureData,
    data: successData,
    error: errorResponseData,
    isPending,
    mutate,
    mutateAsync,
    message,
    isError,
    isSuccess,
    clearData: reset,
    accessToken,
    refreshToken,
  };
}

export function FetchDataByMutate(url?: string) {
  const queryClient = useQueryClient();
  let message: string | undefined;
  let successData;
  let errorResponseData;

  const { data, error, isError, isSuccess, isPending, reset, mutate, mutateAsync } =
    useMutation({
      mutationKey: [url],
      mutationFn: requestPayload({ url: url, method: 'POST' }),
      onSuccess: (response: any) => {
        const formattedData = onSuccessFetch(response);
        successData = formattedData;
        message = formattedData?.description || formattedData?.message;
        queryClient.invalidateQueries({ queryKey: [url] });
      },
      onError: (error: any) => {
        const errorData = onError(error);
        errorResponseData = errorData;
        message = extractErrorMessage(errorData);
      },
    });

  const errorData: ApiResponse['error'] | undefined = error?.response?.data;
  const responseData: ApiResponse['data'] | undefined = data;

  if (errorData) {
    message = extractErrorMessage(errorData);
    errorResponseData = extractErrorData(errorData);
  }
  if (responseData) {
    successData = extractData(responseData?.data).formattedData;
    message = successData?.Description || successData?.message;
  }

  return {
    data: successData,
    error: errorResponseData,
    isPending,
    mutate,
    mutateAsync,
    message,
    isError,
    isSuccess,
    clearData: reset,
  };
}

export function PutMutate(url?: string, invUrl?: string) {
  const queryClient = useQueryClient();
  let message: string | undefined;
  let successData;
  let errorResponseData;
  let pureData;
  let accessToken;
  let refreshToken;

  const { data, error, isError, isSuccess, isPending, mutate, mutateAsync, reset } =
    useMutation({
      mutationKey: [url],
      mutationFn: requestPayload({ url: url, method: 'PUT' }),
      onSuccess: async (response: any) => {
        const formattedData: any = onSuccess(await response);
        successData = formattedData;
        message = formattedData?.Description || formattedData?.message;
        accessToken = formattedData?.access_token;
        queryClient.invalidateQueries({ queryKey: [invUrl] });
      },
      onError: async (error: any) => {
        const errorData = onError(error);
        errorResponseData = errorData;
        message = extractErrorMessage(await errorData);
      },
    });

  const errorData: ApiResponse['error'] | undefined = error;
  const responseData: ApiResponse['data'] | undefined = data;

  if (errorData) {
    message = extractErrorMessage(errorData);
    errorResponseData = extractErrorData(errorData);
  }
  if (responseData) {
    successData = extractData(responseData?.data).formattedData;
    pureData = extractData(responseData?.data).unformattedData;
    message = successData?.message;
    accessToken = successData?.data?.access_token;
    refreshToken = successData?.data?.refresh_token;
  }

  return {
    pureData: pureData,
    data: successData,
    error: errorResponseData,
    isPending,
    mutate,
    mutateAsync,
    message,
    isError,
    isSuccess,
    clearData: reset,
    accessToken,
    refreshToken,
  };
}

export function DeleteMutate(url?: string, invUrl?: string) {
  const queryClient = useQueryClient();
  let message: string | undefined;
  let successData;
  let errorResponseData;
  let pureData;

  const { data, error, isError, isSuccess, isPending, mutate, mutateAsync, reset } =
    useMutation({
      mutationKey: [url],
      mutationFn: requestPayload({ url: url, method: 'DELETE' }),
      onSuccess: async (response: any) => {
        const formattedData: any = onSuccess(await response);
        successData = formattedData;
        message = formattedData?.Description || formattedData?.message;
        queryClient.invalidateQueries({ queryKey: [invUrl] });
      },
      onError: async (error: any) => {
        const errorData = onError(error);
        errorResponseData = errorData;
        message = extractErrorMessage(await errorData);
      },
    });

  const errorData: ApiResponse['error'] | undefined = error;
  const responseData: ApiResponse['data'] | undefined = data;

  if (errorData) {
    message = extractErrorMessage(errorData);
    errorResponseData = extractErrorData(errorData);
  }
  if (responseData) {
    successData = extractData(responseData?.data).formattedData;
    pureData = extractData(responseData?.data).unformattedData;
    message = successData?.message;
  }

  return {
    pureData: pureData,
    data: successData,
    error: errorResponseData,
    isPending,
    mutate,
    mutateAsync,
    message,
    isError,
    isSuccess,
    clearData: reset,
  };
}

export function GetMutate(url?: string, invUrl?: string) {
  const queryClient = useQueryClient();
  let message: string | undefined;
  let successData;
  let errorResponseData;
  let pureData;

  const { data, error, isError, isSuccess, isPending, mutate, mutateAsync, reset } =
    useMutation({
      mutationKey: [url],
      mutationFn: requestPayload({ url: url, method: 'GET' }),
      onSuccess: async (response: any) => {
        const formattedData: any = onSuccess(await response);
        successData = formattedData;
        message = formattedData?.Description || formattedData?.message;
        queryClient.invalidateQueries({ queryKey: [invUrl] });
      },
      onError: async (error: any) => {
        const errorData = onError(error);
        errorResponseData = errorData;
        message = extractErrorMessage(await errorData);
      },
    });

  const errorData: ApiResponse['error'] | undefined = error;
  const responseData: ApiResponse['data'] | undefined = data;

  if (errorData) {
    message = extractErrorMessage(errorData);
    errorResponseData = extractErrorData(errorData);
  }
  if (responseData) {
    successData = extractData(responseData?.data).formattedData;
    pureData = extractData(responseData?.data).unformattedData;
    message = successData?.message;
  }

  return {
    pureData: pureData,
    data: successData,
    error: errorResponseData,
    isPending,
    mutate,
    mutateAsync,
    message,
    isError,
    isSuccess,
    clearData: reset,
  };
}
